import type { NodePosition } from '@/features/flows/types.ts'

/**
 * The one place that knows how a stored grid cell becomes a pixel — and back.
 *
 * **A cell is a slot, not a pixel unit**, and that is the whole reason this file
 * exists. Directus's canvas stores fine-grained cells (18 px) and draws a tile
 * 14 of them wide, so a tile spans many cells. Core's `positionForChild` places
 * a child at `parent.x + 1` and the seeded worked example sits at x = 1, 2, 3 —
 * one node per cell. Reading those numbers as 18 px steps would stack the whole
 * flow inside a single card's width.
 *
 * So a cell is sized to a card plus its gutter, and the lattice a drag snaps to
 * is a lattice of card positions. That is also what makes the snap legible: a
 * drag that does not cross half a slot does not move the node, which is the
 * intended answer for a lattice this coarse.
 */

/** One column: the card's width plus the gutter between two columns. */
export const CELL_X = 280
/** One row. Taller than the tallest card body so a reject child clears its sibling. */
export const CELL_Y = 190
/** The card itself. The cell is wider by exactly the gutter. */
export const CARD_WIDTH = 240

/**
 * The lowest cell the canvas will ever *write*, per D9's "clamped at 1".
 *
 * It clamps what a drag produces, never what was read: the engine's
 * `newFlowGraph()` mints its trigger at `{ x: 0, y: 0 }`, and silently
 * rewriting a stored position on load would make opening a flow a change to it.
 * The first drag of that trigger moves it into the clamped range, which is the
 * moment a person actually asked for a position.
 */
const MIN_CELL = 1

/**
 * A cell clamped to the lowest one the canvas will write.
 *
 * Exported so the arrow-key move and {@link toCell} share one clamp rather than
 * repeating the literal — they are the same rule, and a second copy is where
 * "clamped at 1" starts meaning two things.
 */
export const clampCell = (position: NodePosition): NodePosition => ({
  x: Math.max(MIN_CELL, position.x),
  y: Math.max(MIN_CELL, position.y),
})

export const toPixels = (position: NodePosition): { x: number; y: number } => ({
  x: position.x * CELL_X,
  y: position.y * CELL_Y,
})

/**
 * A pixel position as the cell it belongs to: `Math.round(px / cell)` (D9),
 * clamped at {@link MIN_CELL}.
 *
 * Rounding the absolute position rather than the drag delta is the same answer
 * — a node always sits at an exact multiple of the cell, so `round(x/cell)` and
 * `origin + round(delta/cell)` agree — and it needs no drag-start bookkeeping.
 */
export const toCell = (px: { x: number; y: number }): NodePosition =>
  clampCell({ x: Math.round(px.x / CELL_X), y: Math.round(px.y / CELL_Y) })

/** True when two cells name the same slot — the test that keeps a no-op drag clean. */
export const sameCell = (a: NodePosition, b: NodePosition): boolean => a.x === b.x && a.y === b.y
